export type LatestRequestResult<T> =
  | { superseded: true }
  | { superseded: false; value: T };

/**
 * Keeps only the newest of a list's requests. Starting a request aborts the one
 * still in flight, so a slow response for old filters can never overwrite the
 * results of newer ones. A superseded or aborted request resolves to
 * `{ superseded: true }`; any other failure of the newest request is rethrown.
 *
 * Wrap instances in `markRaw` when storing them in component data: Vue's
 * reactive proxy breaks the native AbortController methods.
 */
export class LatestRequest {
  private controller: AbortController | null = null;

  async run<T>(
    request: (signal: AbortSignal) => Promise<T>
  ): Promise<LatestRequestResult<T>> {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;

    try {
      const value = await request(controller.signal);
      return this.controller === controller
        ? { superseded: false, value }
        : { superseded: true };
    } catch (error) {
      // Aborting always replaces or clears the current controller, so this
      // also covers the error axios raises for an aborted request.
      if (this.controller !== controller) {
        return { superseded: true };
      }
      throw error;
    } finally {
      if (this.controller === controller) {
        this.controller = null;
      }
    }
  }

  abort(): void {
    this.controller?.abort();
    this.controller = null;
  }
}
